import Box from "@mui/material/Box";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ClearIcon from "@mui/icons-material/Clear";
import Grid from "@mui/material/Grid";
import * as turf from "@turf/turf";
import { actionMapZoomFeaturesUpdated, actionMapDrawModeUpdated } from "../Map";
import { actionAppBarFloorUpdated } from "../AppBar";
import { actionDrawUpdateGroupFeature } from "./drawRedux";
import { Tooltip } from "@mui/material";

const feature_ = (theme) => ({
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
  "&:hover svg": {
    visibility: "visible",
  },
  alignItems: "center",
});

const featureDelete_ = (theme) => ({
  visibility: "hidden",
  "&:hover": {
    cursor: "pointer",
  },
});

const floor_ = (theme) => ({
  color: "rgba(0, 0, 0, 0.55)",
  fontSize: "0.7rem",
});

export default function Feature({ feature, onColorPickerClick }) {
  const dispatch = useDispatch();

  const { id, properties } = feature;
  const { groupId, name, floor } = properties || {};

  const onClickFeature = () => {
    dispatch(actionMapZoomFeaturesUpdated({ data: feature }));
    dispatch(actionAppBarFloorUpdated(floor === "BLDG" ? "" : floor));
  };

  const onRemoveFeature = () => {
    dispatch(actionDrawUpdateGroupFeature("delete", { id, groupId }));
    dispatch(actionMapDrawModeUpdated({ clearDrawing: true, data: id }));
  };

  return (
    <Box>
      <Grid container sx={feature_}>
        <Grid size={{ xs: 1 }} sx={featureDelete_}>
          <ClearIcon fontSize="small" onClick={onRemoveFeature} />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Button size="small" color="primary" onClick={onClickFeature}>
            {name}
          </Button>
          <RoomRecordNumber feature={feature} />
        </Grid>
        <Grid container size={{ xs: 4 }} sx={{ alignItems: "center" }}>
          <Measurement feature={feature} />
        </Grid>
        <Grid size={{ xs: 2 }}>
          <Typography variant="button" sx={floor_}>
            {floor}
          </Typography>
        </Grid>
        <Grid size={{ xs: 1 }}>
          <Color feature={feature} onColorPickerClick={onColorPickerClick} />
        </Grid>
      </Grid>
    </Box>
  );
}

function Measurement({ feature = {} }) {
  const { geometry, properties } = { geometry: {}, properties: {}, ...feature };
  const { type } = geometry;

  const isPolygon = type === "Polygon" || type === "MultiPolygon";
  const isLine = type === "LineString" || type === "MultiLineString";
  const isPoint = type === "Point" || type === "MultiPoint";
  let measurement;

  if (isPolygon) {
    measurement = (
      <PolygonMeasurement geometry={geometry} properties={properties} />
    );
  } else if (isLine) {
    measurement = <LineMeasurement geometry={geometry} />;
  } else if (isPoint) {
    measurement = <PointMeasurement geometry={geometry} />;
  }

  return <>{measurement}</>;
}

function RoomRecordNumber({ feature }) {
  const { properties } = { properties: {}, ...feature };
  const style = {
    color: "grey",
    fontSize: 10,
    letterSpacing: "initial",
  };

  if (properties && properties.rmrecnbr) {
    return (
      <Tooltip
        describeChild
        title={`Room Record Number: ${properties.rmrecnbr}`}
        arrow
      >
        <span style={style}> {properties.rmrecnbr}</span>
      </Tooltip>
    );
  }

  return null;
}

const grid_ = (theme) => ({
  lineHeight: 1,
});

const btn_ = (theme) => ({
  fontSize: "0.6rem",
  padding: "0 2px",
  borderRadius: "0px",
  color: "black",
});

const display_ = (theme) => ({
  width: 15,
  height: 15,
  borderRadius: "50%",
  "&:hover": {
    cursor: "pointer",
  },
});

function Color({ feature, onColorPickerClick }) {
  const { color } = feature.properties || {};
  const bgColor = { backgroundColor: color || "#3bb2d0" };

  const onClick = () => {
    onColorPickerClick(feature);
  };

  return <Box style={bgColor} sx={display_} onClick={onClick}></Box>;
}

function PolygonMeasurement({ geometry, properties = {} }) {
  const SQM_TO_SQFT = 10.76391042;

  const [unit, setUnit] = useState("sqft");
  const [value, setValue] = useState(
    properties.rmsqrft || turf.area(geometry) * SQM_TO_SQFT,
  );

  useEffect(() => {
    if (unit === "sqft") {
      setValue(properties.rmsqrft || turf.area(geometry) * SQM_TO_SQFT);
    } else if (unit === "sqm") {
      setValue(
        properties.rmsqrft
          ? properties.rmsqrft / SQM_TO_SQFT
          : turf.area(geometry),
      );
    }
  }, [geometry, properties.rmsqrft, unit]);

  const onClick = () => {
    setUnit(unit === "sqft" ? "sqm" : "sqft");
  };

  return (
    <Grid size={{ xs: 12 }} sx={grid_}>
      <Button size="small" sx={btn_} onClick={onClick}>
        {`${value.toFixed(2)} ${unit}`}
      </Button>
    </Grid>
  );
}

function LineMeasurement({ geometry }) {
  const KM_TO_FT = 3280.84;
  const KM_TO_M = 1000;
  const lineGeometry = toLineGeometry(geometry);

  const [unit, setUnit] = useState("ft");
  const [value, setValue] = useState(turf.length(lineGeometry) * KM_TO_FT);

  useEffect(() => {
    const value =
      unit === "ft"
        ? turf.length(lineGeometry) * KM_TO_FT
        : turf.length(lineGeometry) * KM_TO_M;
    setValue(value);
  }, [lineGeometry, unit]);

  const onClick = () => {
    setUnit(unit === "ft" ? "m" : "ft");
  };

  return (
    <Grid size={{ xs: 12 }} sx={grid_}>
      <Button size="small" sx={btn_} onClick={onClick}>
        {`${value.toFixed(2)} ${unit}`}
      </Button>
    </Grid>
  );
}

function PointMeasurement({ geometry }) {
  const [lng, lat] = geometry.coordinates;

  return (
    <>
      <Grid size={{ xs: 12 }} sx={grid_}>
        {/* Override div/tabindex so Siteimprove does not think these
            are interactive elements (since there is no onClick). */}
        <Button component="div" tabindex="" size="small" sx={btn_}>
          {`${lng.toFixed(6)} Lng`}
        </Button>
      </Grid>
      <Grid size={{ xs: 12 }} sx={grid_}>
        <Button component="div" tabindex="" size="small" sx={btn_}>
          {`${lat.toFixed(6)} Lat`}
        </Button>
      </Grid>
    </>
  );
}

function toLineGeometry(geometry = {}) {
  const lineGeometry = { ...geometry };
  const lastIndex = lineGeometry.coordinates.length - 1;
  // remove last coordinate since mapbox automatically close linestring coordinate and becomes a polygon
  if (lineGeometry.coordinates[0] === lineGeometry.coordinates[lastIndex]) {
    lineGeometry.coordinates.splice(lastIndex, 1);
  }
  return lineGeometry;
}
